<template>
  <div class="survey-app">
    <component
      :is="currentComponent"
      v-bind="currentComponentProps"
      :survey="survey"
      @accept="handleAcceptTerms"
      @continue="handleStartSurvey"
      @start="handleStartQuestions"
      @complete="handleComplete"
      @back="handleBack"
    />
  </div>
</template>

<script>
import SurveyLoading from "../components/SurveyLoading.vue";
import TermsAndConditions from "../sections/survey/TermsAndConditions.vue";
import Welcome from "../sections/survey/Welcome.vue";
import Instructions from "../sections/survey/Instructions.vue";
import QuestionGroup from "../sections/survey/QuestionGroup.vue";
import OpenEndedQuestions from "../sections/survey/OpenEndedQuestions.vue";
import ThankYou from "../sections/survey/ThankYou.vue";
import SurveyError from "../sections/survey/SurveyError.vue";
import SurveyClosed from "../sections/survey/SurveyClosed.vue";
import SurveyNotFound from "../sections/survey/SurveyNotFound.vue";
import SurveyNotActive from "../sections/survey/SurveyNotActive.vue";
import SurveyAlreadyCompleted from "../sections/survey/SurveyAlreadyCompleted.vue";

import { surveyData } from "../helpers";
import { fetchSurvey, saveQuestions, saveTermsAndConditions } from "../api";

export default {
  name: "SurveyApp",
  components: {
    SurveyLoading,
    TermsAndConditions,
    Welcome,
    Instructions,
    QuestionGroup,
    OpenEndedQuestions,
    ThankYou,
    SurveyError,
    SurveyClosed,
    SurveyNotFound,
    SurveyNotActive,
    SurveyAlreadyCompleted,
  },
  data() {
    return {
      survey: null,
      currentStep: "loading",
      acceptedTerms: false,
      surveyData: surveyData,
      steps: [],
      stepsOpen: [],
    };
  },
  computed: {
    currentComponent() {
      const questionsComponent = this.steps.reduce((acc, key) => {
        acc[key] = "QuestionGroup";
        return acc;
      }, {});

      const questionsOpenComponent = this.stepsOpen.reduce((acc, key) => {
        acc[key] = "OpenEndedQuestions";
        return acc;
      }, {});

      const componentMap = {
        loading: "SurveyLoading",
        terms: "TermsAndConditions",
        welcome: "Welcome",
        instructions: "Instructions",
        ...questionsComponent,
        ...questionsOpenComponent,
        "thank-you": "ThankYou",
        error: "SurveyError",
        closed: "SurveyClosed",
        "not-active": "SurveyNotActive",
        "not-found": "SurveyNotFound",
        "already-completed": "SurveyAlreadyCompleted",
      };

      return componentMap[this.currentStep];
    },
    currentComponentProps() {
      if (this.currentStep.startsWith("group-")) {
        const type = this.survey.typeSurvey;
        const groupNumber = parseInt(this.currentStep.split("-")[1]);
        const { questions, ...group } = this.surveyData.groups[type][
          groupNumber
        ];

        return {
          group: {
            ...group,
            sizeGroups: this.surveyData.groups[type].length,
          },
          groupNumber,
          questions,
        };
      }

      if (this.currentStep.startsWith("openEnded-")) {
        const type = this.survey.typeSurvey;
        const groupNumber = parseInt(this.currentStep.split("-")[1]);
        const { questions, ...group } = this.surveyData.openEndedQuestions[
          type
        ][groupNumber];

        return {
          group: {
            ...group,
            sizeGroups: this.surveyData.openEndedQuestions[type].length,
          },
          groupNumber,
          questions,
        };
      }

      return {};
    },
  },
  mounted() {
    const id = this.$route.params.id;
    fetchSurvey(id)
      .then((response) => {
        // Validar respuesta
        if (!response) {
          this.currentStep = "not-found";
          return;
        }

        this.survey = response;

        this.steps = this.surveyData.groups[response.typeSurvey].map(
          (x) => x.id,
        );
        this.stepsOpen = this.surveyData.openEndedQuestions[
          response.typeSurvey
        ].map((x) => x.id);

        const nextStep = this.determineNextStep(response);

        if (
          response.process.status !== "active" &&
          nextStep !== "already-completed"
        ) {
          this.currentStep = "not-active";
          return;
        }

        if (nextStep) {
          this.currentStep = nextStep;
        } else {
          this.currentStep = "welcome";
        }
      })
      .catch((error) => {
        console.error("Error al obtener la encuesta:", error);
        this.currentStep = "error";
      });
  },
  methods: {
    handleAcceptTerms(value) {
      this.acceptedTerms = !!value;

      const termsAndConditions = {
        accepted: this.acceptedTerms,
        acceptedAt: new Date(),
      };

      saveTermsAndConditions({
        id: this.survey._id,
        termsAndConditions,
      })
        .then(() => {
          this.$store.dispatch("notify", {
            message: `Terminos ${
              this.acceptedTerms ? "aceptados" : "rechazados"
            }.`,
            type: "is-success",
          });
          if (this.acceptedTerms) {
            this.currentStep = "instructions";
          } else {
            this.currentStep = "thank-you";
          }
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al aceptar los terminos y condiciones.",
            type: "is-danger",
          });
        });
    },
    handleStartSurvey() {
      this.currentStep = "terms";
    },
    handleStartQuestions() {
      this.currentStep = this.steps[0];
    },
    handleBack(data) {
      const nextSteps = [...this.steps, ...this.stepsOpen];

      if (data.type === "group") {
        this.currentStep = nextSteps[data.groupNumber - 1];
      } else if (data.type === "open-ended") {
        this.currentStep = nextSteps.reverse()[data.groupNumber + 1];
      }
    },
    async handleComplete(data) {
      if (data.type === "group") {
        await this.handleGroupComplete(data.groupNumber, data.answers);
      } else if (data.type === "open-ended") {
        await this.handleOpenEndedComplete(data.groupNumber, data.answers);
      }
    },
    async handleGroupComplete(groupNumber, answers) {
      const nextSteps = [...this.steps, ...this.stepsOpen];

      saveQuestions({
        id: this.survey._id,
        responses: answers,
      })
        .then(() => {
          this.currentStep = nextSteps[groupNumber + 1];
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al guardar la encuesta.",
            type: "is-danger",
          });
        });
    },
    async handleOpenEndedComplete(groupNumber, answers) {
      const nextSteps = [...this.stepsOpen, "thank-you"];
      const finished = this.stepsOpen.length === groupNumber + 1;

      saveQuestions({
        id: this.survey._id,
        finished,
        openQuestions: answers,
      })
        .then(() => {
          this.currentStep = nextSteps[groupNumber + 1];
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al guardar la encuesta.",
            type: "is-danger",
          });
        });
    },
    // Helpers
    determineNextStep(response) {
      let result = null;
      const { termsAndConditions, responses } = response || {};
      const total = responses ? Object.keys(responses).length : 0;

      // Hacer validación de closed cuando se cierra el proceso
      if (["finished", "calculated"].includes(response.status)) {
        return "already-completed";
      }
      if (response.status === "disagreement") return null;
      if (response.status === "expired") return "closed";

      if (!termsAndConditions.accepted) return null;
      if (termsAndConditions.accepted && !total) return "instructions";
      // if (!responses || total === 0) return null;

      if (total < this.steps.length) {
        const lastKey = Object.keys(responses).pop();
        return lastKey ? `group-${parseInt(lastKey) + 1}` : "not-found";
      }
      if (total === this.steps.length) result = this.stepsOpen[0];

      return result;
    },
  },
  errorCaptured(err, vm, info) {
    console.error("🚨 Error capturado:", err);
    console.log("Componente:", vm);
    console.log("Información:", info);
    this.currentStep = "error";
  },
};
</script>

<style scoped>
.survey-app {
  min-height: 100vh;
  background-color: #f5f5f5;
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/views/Survey.vue